import { useMemo, useState } from 'react';
import {
  canastaCheckMelds,
  canastaIsCanasta,
  canastaIsNaturalCanasta,
  canastaIsWild,
  canastaPartnerOf,
  canastaTakeError,
  canastaTeamOf,
  rankLabel,
  type CanastaLegal,
  type CanastaMeld,
  type CanastaMeldGroup,
  type CanastaView,
  type Rank,
} from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { toast } from '../store/ui';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const rankOfId = (id: string): number => {
  const c = id.split(':')[0]!;
  return 'JK' === c ? 99 : '23456789TJQKA'.indexOf(c[0]!) + 2;
};

/** Hand order for melding: wild cards last, then by rank (high first) so sets sit together. */
const meldSort = (cards: readonly string[]): string[] =>
  [...cards].sort((a, b) => {
    const wa = canastaIsWild(a) ? 1 : 0;
    const wb = canastaIsWild(b) ? 1 : 0;
    if (wa !== wb) return wa - wb;
    return rankOfId(b) - rankOfId(a) || a.localeCompare(b);
  });

function MeldChips({ melds, t }: { melds: readonly CanastaMeld[]; t: ReturnType<typeof useT> }) {
  if (melds.length === 0) return <span className="dim">{t('cnt.noMeld')}</span>;
  return (
    <>
      {melds.map((m) => {
        const wilds = m.cards.filter(canastaIsWild).length;
        const canasta = canastaIsCanasta(m);
        return (
          <span key={m.rank} className={`pill ${canasta ? 'accent' : ''}`} title={canasta ? t('cnt.canasta') : undefined}>
            {rankLabel(m.rank as Rank)}×{m.cards.length}
            {wilds > 0 ? ` (${wilds}w)` : ''}
            {canasta ? (canastaIsNaturalCanasta(m) ? ' ★' : ' ✓') : ''}
          </span>
        );
      })}
    </>
  );
}

export function CanastaTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as CanastaView;
  const legal = useGame((s) => s.legal) as CanastaLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const [selected, setSelected] = useState<string[]>([]);
  const [pickRank, setPickRank] = useState<string[] | null>(null);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? canastaTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && Boolean(turn?.seats.includes(mySeat));
  const hand = useMemo(() => meldSort(view.hand), [view.hand]);
  const twoRows = hand.length > 12;
  const size = twoRows || narrow ? 'md' : 'lg';
  const top = view.pile.at(-1);
  const myMelds = view.melds[myTeam] ?? [];
  const theirMelds = view.melds[1 - myTeam] ?? [];
  const handOver = view.phase === 'HAND_END' || view.phase === 'MATCH_END';
  const lastResult = view.handResults.at(-1);

  const toggle = (c: string) => setSelected((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));
  const done = (ok: boolean | void) => {
    if (ok !== false) setSelected([]);
  };

  /** Groups for a meld from the selection: one rank of naturals (wild cards ride along) or several natural ranks. */
  const buildGroups = (cards: string[], wildRank?: number): CanastaMeldGroup[] | string => {
    const wilds = cards.filter(canastaIsWild);
    const naturals = cards.filter((c) => !canastaIsWild(c));
    const ranks = [...new Set(naturals.map(rankOfId))];
    if (ranks.length === 0) {
      if (wildRank === undefined) return 'pick';
      return [{ rank: wildRank, cards: wilds }];
    }
    if (ranks.length === 1) return [{ rank: ranks[0]!, cards: [...naturals, ...wilds] }];
    if (wilds.length > 0) return t('cnt.wildNeedRank');
    return ranks.map((rank) => ({ rank, cards: naturals.filter((c) => rankOfId(c) === rank) }));
  };

  const doMeld = (wildRank?: number) => {
    if (mySeat === null) return;
    const groups = buildGroups(selected, wildRank);
    if (typeof groups === 'string') {
      if (groups === 'pick') {
        if (myMelds.length > 0) setPickRank(selected);
        else toast(t('cnt.wildNeedRank'), 'error');
      } else toast(groups, 'error');
      return;
    }
    const err = canastaCheckMelds({ hand: view.hand, groups, teamMelds: myMelds, melded: view.melded[myTeam] ?? false, minMeld: view.initialMin[myTeam] ?? 50 });
    if (err) {
      toast(err, 'error');
      return;
    }
    void act({ type: 'meld', groups }).then(done);
  };

  const doTake = () => {
    if (!top) return;
    const rank = rankOfId(top);
    const naturals = view.hand.filter((c) => !canastaIsWild(c) && rankOfId(c) === rank);
    const wilds = view.hand.filter(canastaIsWild);
    let cards = selected;
    if (cards.length === 0) {
      cards = [...naturals];
      const exists = myMelds.some((m) => m.rank === rank);
      if (!view.pileFrozen && !exists && naturals.length === 1 && wilds[0]) cards = [...naturals, wilds[0]];
      if (!view.pileFrozen && exists && naturals.length === 0 && wilds[0]) cards = [wilds[0]];
    }
    const err = canastaTakeError({ hand: view.hand, pile: view.pile, frozen: view.pileFrozen, teamMelds: myMelds, melded: view.melded[myTeam] ?? false, cards });
    if (err) {
      toast(err, 'error');
      return;
    }
    void act({ type: 'takePile', cards }).then(done);
  };

  const drawing = myTurn && legal?.kind === 'draw';
  const playing = myTurn && legal?.kind === 'play';
  const cardEls = hand.map((c) => (
    <PlayingCard key={c} id={c} size={size} wild={canastaIsWild(c)} selected={selected.includes(c)} playable={playing || (drawing && legal?.kind === 'draw' && legal.canTakePile)} onClick={() => (myTurn ? toggle(c) : undefined)} />
  ));
  const split = twoRows ? Math.ceil(cardEls.length / 2) : cardEls.length;
  const need = view.initialMin[myTeam] ?? 50;

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[canastaTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === canastaPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {!handOver && <span className="tag">{view.handCounts[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('cnt.hand', { n: view.round })} · {t('cnt.stock', { n: view.stockCount })}
          </div>
          {handOver && lastResult ? (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{view.wentOut !== null ? t('cnt.wentOut', { name: name(view.wentOut) }) : t('cnt.stockOut')}</strong>
              <div className="col" style={{ marginTop: 6, gap: 4 }}>
                {lastResult.map((r, team) => (
                  <span key={team} className={`pill ${r.total > 0 ? 'accent' : ''}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: {r.total > 0 ? '+' : ''}{r.total}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="row" style={{ gap: 14, alignItems: 'flex-end', justifyContent: 'center' }}>
                <div className="col" style={{ alignItems: 'center', gap: 4 }}>
                  <PlayingCard faceDown size={narrow ? 'sm' : 'md'} />
                </div>
                <div className="col" style={{ alignItems: 'center', gap: 4 }} aria-label={t('cnt.pile', { n: view.pile.length })}>
                  {top ? <PlayingCard id={top} size={narrow ? 'sm' : 'md'} wild={canastaIsWild(top)} /> : <span className={`pcard ${narrow ? 'sm' : 'md'}`} />}
                  <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>
                    {t('cnt.pile', { n: view.pile.length })}
                    {view.pileFrozen ? ` · ${t('cnt.frozen')}` : ''}
                  </span>
                </div>
              </div>
              <div className="col" style={{ gap: 4, alignItems: 'center' }}>
                <div className="row wrap" style={{ justifyContent: 'center', gap: 4 }} aria-label={t('cnt.ourMelds')}>
                  <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{t('cnt.ourMelds')} ({view.redThrees[myTeam]?.length ?? 0}×3♥)</span>
                  <MeldChips melds={myMelds} t={t} />
                </div>
                <div className="row wrap" style={{ justifyContent: 'center', gap: 4 }} aria-label={t('cnt.theirMelds')}>
                  <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{t('cnt.theirMelds')} ({view.redThrees[1 - myTeam]?.length ?? 0}×3♥)</span>
                  <MeldChips melds={theirMelds} t={t} />
                </div>
                {!(view.melded[myTeam] ?? false) && <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{t('cnt.need', { n: need })}</span>}
              </div>
            </>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        <div role="group" aria-label="Your hand" className="hand-rows">
          <Hand size={size} label={twoRows ? 'Your hand, row 1' : 'Your hand'} maxStep={0.6}>
            {cardEls.slice(0, split)}
          </Hand>
          {twoRows && (
            <Hand size={size} label="Your hand, row 2" maxStep={0.6}>
              {cardEls.slice(split)}
            </Hand>
          )}
        </div>
        <div className="action-bar">
          {drawing && (
            <>
              <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'draw' }).then(done)}>
                {t('cnt.draw')}
              </button>
              <button className="btn gold" disabled={acting || legal?.kind !== 'draw' || !legal.canTakePile} onClick={doTake}>
                {t('cnt.take')}
              </button>
            </>
          )}
          {playing && (
            <>
              <button className="btn primary" disabled={acting || selected.length === 0} onClick={() => doMeld()}>
                {t('cnt.meld')}
              </button>
              <button className="btn gold" disabled={acting || selected.length !== 1} onClick={() => selected[0] && void act({ type: 'discard', card: selected[0] }).then(done)}>
                {t('cnt.discard')}
              </button>
            </>
          )}
          {myTurn && selected.length > 0 && (
            <button className="btn ghost sm" onClick={() => setSelected([])}>
              {t('cnt.clear')}
            </button>
          )}
        </div>
      </div>
      <Modal open={Boolean(pickRank)} onClose={() => setPickRank(null)} title={t('cnt.pickRank')}>
        <div className="row wrap">
          {myMelds.map((m) => (
            <button
              key={m.rank}
              className="btn primary"
              onClick={() => {
                setPickRank(null);
                doMeld(m.rank);
              }}
            >
              {rankLabel(m.rank as Rank)}×{m.cards.length}
            </button>
          ))}
        </div>
      </Modal>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.handResults.map((row, r) => (
              <tr key={r}>
                <td>H{r + 1}</td>
                {row.map((x, team) => (
                  <td key={team} className="num">
                    {x.total}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {x.melds}+{x.canastas}+{x.redThrees}+{x.goOut}−{x.hands}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('cnt.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
